import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShoppingCart, RefreshCw, Zap, Package, Wifi, WifiOff,
  Bell, Play, Trash2, Plus, X, CheckCircle, AlertTriangle,
  ArrowDownToLine, ArrowUpFromLine, Activity
} from 'lucide-react';

const API  = process.env.REACT_APP_BACKEND_URL + '/api/wms/shopee';
const SIM  = process.env.REACT_APP_BACKEND_URL + '/api/shopee-sim';
import { formatDT } from '../WMSShared';

const STATUS_COLOR = {
  READY_TO_SHIP: 'bg-blue-100 text-blue-700',
  SHIPPED:        'bg-cyan-100 text-cyan-700',
  COMPLETED:      'bg-emerald-100 text-emerald-700',
  CANCELLED:      'bg-red-100 text-red-700',
  UNPAID:         'bg-amber-100 text-amber-700',
};

export default function ShopeeTab({ products, tz = 'Asia/Singapore' }) {
  const [status, setStatus]         = useState({});
  const [orders, setOrders]         = useState([]);
  const [shopeeOrders, setShopeeOrders] = useState([]);
  const [shopeeProducts, setShopeeProducts] = useState([]);
  const [webhookLogs, setWebhookLogs]   = useState([]);
  const [loading, setLoading]       = useState({});
  const [results, setResults]       = useState({});
  const [subTab, setSubTab]         = useState('orders');

  // Test order form state
  const [form, setForm] = useState({
    buyer_username: 'john_tan_sg',
    buyer_phone: '+65 9123 4567',
    address: 'Blk 123 Toa Payoh Lorong 1',
    postal_code: '310123',
    payment_method: 'ShopeePay',
    items: [],
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [itemQty, setItemQty]       = useState(1);
  const [showOrderForm, setShowOrderForm] = useState(false);

  const load = async () => {
    try {
      const [st, so, sp, wl] = await Promise.all([
        axios.get(`${API}/status`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/webhook/logs`),
      ]);
      setStatus(st.data);
      setShopeeOrders(so.data);
      setShopeeProducts(sp.data);
      setWebhookLogs(wl.data.slice(0, 20));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const run = async (key, fn) => {
    setLoading(p => ({ ...p, [key]: true }));
    setResults(p => ({ ...p, [key]: null }));
    try {
      const res = await fn();
      setResults(p => ({ ...p, [key]: res.data }));
      load();
    } catch (e) {
      setResults(p => ({ ...p, [key]: { error: e.response?.data?.detail || 'Failed' } }));
    }
    setLoading(p => ({ ...p, [key]: false }));
  };

  const addItem = () => {
    const prod = products.find(p => p.product_id === selectedProduct);
    if (!prod) return;
    const exists = form.items.find(i => i.wms_product_id === prod.product_id);
    if (exists) return;
    setForm(f => ({
      ...f,
      items: [...f.items, {
        wms_product_id: prod.product_id,
        model_sku:      prod.sku,
        item_name:      prod.name,
        qty:            itemQty,
        price:          prod.price,
      }],
    }));
    setSelectedProduct('');
    setItemQty(1);
  };

  const createTestOrder = async () => {
    if (form.items.length === 0) { alert('Add at least one item'); return; }
    await run('create_order', () =>
      axios.post(`${SIM}/test/create_order`, {
        buyer_username:  form.buyer_username,
        buyer_phone:     form.buyer_phone,
        address:         form.address,
        postal_code:     form.postal_code,
        payment_method:  form.payment_method,
        items: form.items.map(i => ({
          wms_product_id: i.wms_product_id,
          model_sku:      i.model_sku,
          item_name:      i.item_name,
          qty:            i.qty,
          price:          i.price,
        })),
      })
    );
    setForm(f => ({ ...f, items: [] }));
    setShowOrderForm(false);
  };

  const resetSim = async () => {
    if (!window.confirm('Reset all mock Shopee data?')) return;
    await axios.post(`${SIM}/test/reset`);
    load();
  };

  const triggerWebhook = async (order_sn, event_type) => {
    await run('webhook_' + order_sn, () =>
      axios.post(`${API}/webhook/trigger`, { event_type, order_sn })
    );
  };

  const SUBTABS = [
    ['orders',    ShoppingCart, 'Orders'],
    ['products',  Package,      'Products'],
    ['webhooks',  Bell,         'Webhooks'],
    ['simulator', Zap,          'Simulator'],
  ];

  const totalPending = shopeeOrders.filter(o => !o.synced_to_wms).length;

  return (
    <div className="space-y-6">
      {/* ── CONNECTION CARD ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-xl">S</div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-gray-900">{status.shop_name || 'Agora Electronics SG'}</h2>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${status.mode === 'live' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {status.mode === 'live' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                  {status.mode === 'live' ? 'Live API' : 'Simulator Mode'}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Shop ID: {status.shop_id || '999001'} ·
                Last sync: {status.last_order_sync ? formatDT(status.last_order_sync, tz) : 'Never'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {totalPending > 0 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full font-medium">
                {totalPending} orders pending sync
              </span>
            )}
            <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 divide-x border-t">
          {[
            { label: 'Shopee Orders',  value: shopeeOrders.length },
            { label: 'Synced to WMS',  value: status.total_synced ?? 0 },
            { label: 'Products Listed', value: shopeeProducts.length },
            { label: 'Webhook Events', value: status.webhook_events ?? 0 },
          ].map(({ label, value }) => (
            <div key={label} className="px-5 py-3 text-center">
              <p className="text-xl font-bold text-gray-900">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>

        {/* Sync controls */}
        <div className="px-5 py-3 border-t bg-gray-50 flex flex-wrap gap-2">
          <button onClick={() => run('sync_orders', () => axios.post(`${API}/sync/orders`))}
            disabled={loading.sync_orders}
            className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:bg-gray-300">
            <ArrowDownToLine className={`w-4 h-4 ${loading.sync_orders ? 'animate-bounce' : ''}`} />
            {loading.sync_orders ? 'Syncing...' : 'Sync Orders'}
          </button>
          <button onClick={() => run('sync_products', () => axios.post(`${API}/sync/products`))}
            disabled={loading.sync_products}
            className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300">
            <ArrowUpFromLine className={`w-4 h-4 ${loading.sync_products ? 'animate-bounce' : ''}`} />
            {loading.sync_products ? 'Pushing...' : 'Push Products'}
          </button>
          <button onClick={() => run('sync_inventory', () => axios.post(`${API}/sync/inventory`))}
            disabled={loading.sync_inventory}
            className="flex items-center gap-2 border px-4 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading.sync_inventory ? 'animate-spin' : ''}`} />
            {loading.sync_inventory ? 'Syncing...' : 'Sync Stock'}
          </button>
        </div>

        {/* Sync results */}
        {(results.sync_orders || results.sync_products || results.sync_inventory) && (
          <div className="px-5 pb-3">
            {results.sync_orders && !results.sync_orders.error && (
              <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2">
                ✓ Orders: {results.sync_orders.orders_created} imported, {results.sync_orders.orders_skipped} skipped
              </p>
            )}
            {results.sync_products && !results.sync_products.error && (
              <p className="text-xs text-blue-700 bg-blue-50 rounded px-3 py-2 mt-1">
                ✓ Products: {results.sync_products.products_pushed} pushed to Shopee
              </p>
            )}
            {results.sync_inventory && !results.sync_inventory.error && (
              <p className="text-xs text-teal-700 bg-teal-50 rounded px-3 py-2 mt-1">
                ✓ Stock: {results.sync_inventory.inventory_synced} items updated on Shopee
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── SUB-TABS ────────────────────────────────────────────────── */}
      <div className="flex gap-2 border-b pb-3">
        {SUBTABS.map(([id, Icon, label]) => (
          <button key={id} onClick={() => setSubTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${subTab === id ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* ── ORDERS TAB ──────────────────────────────────────────────── */}
      {subTab === 'orders' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Shopee Orders ({shopeeOrders.length})</h3>
          </div>
          {shopeeOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Order SN', 'Buyer', 'Items', 'Total', 'Status', 'WMS Synced', 'Webhook'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shopeeOrders.map(o => (
                    <tr key={o.order_sn} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs font-medium">{o.order_sn}</td>
                      <td className="px-4 py-3 text-xs">{o.buyer_username}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{o.item_list?.length || 0} item(s)</td>
                      <td className="px-4 py-3 font-medium">S${o.total_amount}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[o.order_status] || 'bg-gray-100 text-gray-600'}`}>
                          {o.order_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {o.synced_to_wms
                          ? <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />{o.wms_order_id}</span>
                          : <span className="text-xs text-gray-400">Pending</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => triggerWebhook(o.order_sn, 'order_shipped')}
                            className="text-[10px] border px-1.5 py-0.5 rounded hover:bg-blue-50 text-blue-600">Ship</button>
                          <button onClick={() => triggerWebhook(o.order_sn, 'order_cancelled')}
                            className="text-[10px] border px-1.5 py-0.5 rounded hover:bg-red-50 text-red-600">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-orange-200" />
              <p className="text-sm font-medium text-gray-600">No Shopee orders yet</p>
              <p className="text-xs mt-1">Go to "Simulator" tab to create a test order</p>
            </div>
          )}
        </div>
      )}

      {/* ── PRODUCTS TAB ────────────────────────────────────────────── */}
      {subTab === 'products' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h3 className="font-semibold">Shopee Product Listings ({shopeeProducts.length})</h3>
            <button onClick={() => run('sync_products', () => axios.post(`${API}/sync/products`))}
              disabled={loading.sync_products}
              className="flex items-center gap-2 bg-blue-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-600 disabled:bg-gray-300">
              <ArrowUpFromLine className="w-3 h-3" />Push All WMS Products
            </button>
          </div>
          {shopeeProducts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Shopee ID', 'Product Name', 'SKU', 'Price', 'Stock (Shopee)', 'WMS Product ID', 'Last Sync'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shopeeProducts.map(p => (
                    <tr key={p.item_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">{p.item_id}</td>
                      <td className="px-4 py-3 font-medium text-sm">{p.item_name}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{p.model_sku}</td>
                      <td className="px-4 py-3">S${p.current_price}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${p.stock === 0 ? 'text-red-600' : 'text-gray-800'}`}>{p.stock}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">{p.wms_product_id}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{formatDT(p.synced_at, tz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <Package className="w-10 h-10 mx-auto mb-2 text-blue-200" />
              <p className="text-sm font-medium text-gray-600">No products on Shopee yet</p>
              <p className="text-xs mt-1">Click "Push All WMS Products" to sync your inventory to Shopee</p>
            </div>
          )}
        </div>
      )}

      {/* ── WEBHOOKS TAB ────────────────────────────────────────────── */}
      {subTab === 'webhooks' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <h3 className="font-semibold flex items-center gap-2">
                <Bell className="w-4 h-4 text-purple-500" />Webhook Events ({webhookLogs.length})
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Real-time events from Shopee — simulator fires every 2 min via Celery Beat
              </p>
            </div>
            <button onClick={load} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {webhookLogs.length > 0 ? (
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {webhookLogs.map((log, i) => {
                const code  = log.code;
                const event = code === 3 || code === '3' ? 'order_new' :
                              code === 4 || code === '4' ? 'order_status_update' : `code_${code}`;
                const sn    = log.data?.ordersn || log.data?.order_sn || '—';
                const ts    = log.received_at ? formatDT(log.received_at, tz) : '—';
                return (
                  <div key={i} className="flex items-center gap-4 px-5 py-3">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${log.processed ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${code === 3 || code === '3' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {event}
                        </span>
                        <span className="text-xs text-gray-500 font-mono">{sn}</span>
                        {log.data?.status && <span className="text-xs text-gray-400">→ {log.data.status}</span>}
                      </div>
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">{ts}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              <Bell className="w-10 h-10 mx-auto mb-2 text-purple-200" />
              <p className="text-sm font-medium text-gray-600">No webhook events yet</p>
              <p className="text-xs mt-1">Create a test order in the Simulator tab — webhook fires automatically in ~2 min</p>
            </div>
          )}
          <div className="px-5 py-3 bg-purple-50 border-t text-xs text-purple-700">
            Celery Beat polls every 2 min and fires webhooks for new mock orders. Or use trigger buttons in the Orders tab.
          </div>
        </div>
      )}

      {/* ── SIMULATOR TAB ───────────────────────────────────────────── */}
      {subTab === 'simulator' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-5">
            <h3 className="font-semibold text-orange-800 flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4" />Shopee Order Simulator
            </h3>
            <p className="text-sm text-orange-700 mb-4">
              Create realistic mock Shopee orders to test the full integration flow:
              order → webhook → WMS sync → fulfillment → stock update → Shopee stock push
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-orange-800 mb-4">
              {['1. Create test order below', '2. Webhook fires in ~2 min', '3. WMS auto-creates order', '4. Fulfillment agent packs it'].map((step, i) => (
                <div key={i} className="bg-white/60 rounded-lg p-3 flex items-start gap-2">
                  <span className="font-bold text-orange-500 shrink-0">{i+1}.</span>
                  <span>{step.replace(/^\d+\. /, '')}</span>
                </div>
              ))}
            </div>

            {!showOrderForm ? (
              <button onClick={() => setShowOrderForm(true)}
                className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600">
                <Plus className="w-4 h-4" />Generate Test Shopee Order
              </button>
            ) : (
              <div className="bg-white rounded-xl p-5 space-y-4 border border-orange-200">
                <h4 className="font-semibold text-gray-800">New Test Order</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Buyer Username</label>
                    <input value={form.buyer_username} onChange={e => setForm(f => ({...f, buyer_username: e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                    <input value={form.buyer_phone} onChange={e => setForm(f => ({...f, buyer_phone: e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Delivery Address</label>
                    <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">Postal Code</label>
                    <input value={form.postal_code} onChange={e => setForm(f => ({...f, postal_code: e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>

                {/* Item picker */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Add Products</label>
                  <div className="flex gap-2">
                    <select value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm">
                      <option value="">Select product...</option>
                      {products.map(p => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name} — S${p.price} (WMS stock: {p.stock})
                      </option>
                    ))}
                    </select>
                    <input type="number" min="1" value={itemQty} onChange={e => setItemQty(parseInt(e.target.value) || 1)}
                      className="w-16 border rounded-lg px-3 py-2 text-sm" />
                    <button onClick={addItem} disabled={!selectedProduct}
                      className="bg-blue-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-600 disabled:bg-gray-300">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Items list */}
                {form.items.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr><th className="px-3 py-2 text-left text-xs text-gray-500">Product</th><th className="px-3 py-2 text-xs text-gray-500">Qty</th><th className="px-3 py-2 text-xs text-gray-500">Price</th><th className="px-3 py-2 text-xs text-gray-500">Total</th><th /></tr>
                      </thead>
                      <tbody className="divide-y">
                        {form.items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2 text-xs">{item.item_name}</td>
                            <td className="px-3 py-2 text-xs">{item.qty}</td>
                            <td className="px-3 py-2 text-xs">S${item.price}</td>
                            <td className="px-3 py-2 text-xs font-medium">S${(item.price * item.qty).toFixed(2)}</td>
                            <td className="px-3 py-2"><button onClick={() => setForm(f => ({...f, items: f.items.filter((_, i) => i !== idx)}))}>
                              <X className="w-3 h-3 text-red-400" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-orange-50">
                        <tr><td colSpan={3} className="px-3 py-2 text-xs font-semibold text-right">Grand Total</td>
                          <td className="px-3 py-2 text-sm font-bold text-orange-700">
                            S${form.items.reduce((s, i) => s + i.price * i.qty, 0).toFixed(2)}
                          </td><td /></tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => setShowOrderForm(false)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={createTestOrder} disabled={form.items.length === 0 || loading.create_order}
                    className="flex items-center gap-2 bg-orange-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:bg-gray-300">
                    <Play className="w-4 h-4" />
                    {loading.create_order ? 'Creating...' : 'Create Shopee Order'}
                  </button>
                </div>

                {results.create_order && (
                  <div className={`rounded-lg p-3 text-sm ${results.create_order.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {results.create_order.error || `Order ${results.create_order.order_sn} created! Webhook fires in ~2 min.`}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reset */}
          <div className="bg-white rounded-xl border shadow-sm p-5">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-500" />Reset Simulator
            </h4>
            <p className="text-xs text-gray-500 mb-3">Clears all mock orders and products from the simulator. WMS orders are NOT deleted.</p>
            <button onClick={resetSim} className="text-sm border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50">
              Clear Mock Data
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
